/**
 * Deterministic, client-side insight derivation for the chart header
 * chip strip. No LLM call — every stat here is computed from the
 * cached rows in a single pass, so the chips appear the instant the
 * chart renders.
 *
 * <p>What's NOT in here on purpose:
 * <ul>
 *   <li>Narrative interpretation ("revenue is up 12% YoY") — that's
 *       what the on-demand narrate endpoint is for.</li>
 *   <li>Outlier detection beyond max — meaningful outlier flagging
 *       needs a distribution model and is too noisy to surface
 *       eagerly without confidence thresholds.</li>
 *   <li>Time-series trend lines — same reason; needs context the
 *       chart's row sample doesn't always carry.</li>
 * </ul>
 *
 * <p>The chip strip is intentionally cheap and skim-able. Anything
 * that needs interpretation belongs in the on-demand analysis flow,
 * not here.
 */

export interface DerivedInsights {
  /** Total row count, formatted ("54", "1.2K", "8.4M"). */
  rowsLabel: string;
  /** Total row count, raw. */
  rowsTotal: number;
  /** Number of columns in the dataset. */
  colsTotal: number;
  /** Sum of the chosen numeric column, formatted. Null when no numeric column found. */
  sumLabel: string | null;
  /** Name of the chosen numeric column (for the tooltip / aria-label). */
  measureColumn: string | null;
  /** The single category that contributes the most to the numeric column. */
  topContributor: {
    /** Label from the chosen category column (or the row index if no category column). */
    label: string;
    /** Percentage of the total, formatted ("52.3%"). */
    sharePercent: string;
    /** Raw share, 0..1. */
    shareRaw: number;
  } | null;
  /** Null-cell count across all rows × columns, for a data-quality glance. */
  nullCells: number;
}

/**
 * Compute the chip-strip stats for a dataset. Picks the first column
 * whose values look numeric as the "measure" column, and the first
 * non-numeric column as the "category" column. Falls back gracefully
 * when no numeric column exists (just rows / cols / null count).
 *
 * <p>Numeric detection is value-based, not type-based, because the
 * viz pipeline carries everything as {@code unknown} — a column whose
 * label looks string-y (e.g. "CategoryID") might still hold integers
 * once you peek at the values. We sample up to 10 non-null cells per
 * column to decide.
 */
export function deriveInsights(
  columns: readonly string[],
  rows: ReadonlyArray<ReadonlyArray<unknown>>,
): DerivedInsights {
  const rowsTotal = rows.length;
  const colsTotal = columns.length;
  const nullCells = countNulls(rows);

  const numericIdx = findNumericColumnIndex(columns, rows);
  if (numericIdx < 0) {
    return {
      rowsLabel: compactNumber(rowsTotal),
      rowsTotal,
      colsTotal,
      sumLabel: null,
      measureColumn: null,
      topContributor: null,
      nullCells,
    };
  }

  const categoryIdx = findCategoryColumnIndex(columns, rows, numericIdx);
  const measureName = columns[numericIdx];

  let sum = 0;
  let topLabel: string | null = null;
  let topValue = -Infinity;
  for (let r = 0; r < rows.length; r++) {
    const v = asNumber(rows[r][numericIdx]);
    if (v == null) continue;
    sum += v;
    if (v > topValue) {
      topValue = v;
      topLabel = categoryIdx >= 0
        ? String(rows[r][categoryIdx] ?? `Row ${r + 1}`)
        : `Row ${r + 1}`;
    }
  }

  const shareRaw = sum > 0 && topValue > 0 ? topValue / sum : 0;
  const top = topLabel == null
    ? null
    : {
        label: topLabel,
        sharePercent: formatPercent(shareRaw),
        shareRaw,
      };

  return {
    rowsLabel: compactNumber(rowsTotal),
    rowsTotal,
    colsTotal,
    sumLabel: compactNumber(sum),
    measureColumn: measureName,
    topContributor: top,
    nullCells,
  };
}

/**
 * Sample up to {@link SAMPLE_SIZE} non-null values per column; return
 * the first column where ≥ 80% of sampled values parse as finite
 * numbers. Threshold is a heuristic — strict 100% would reject columns
 * with the occasional "n/a" string sprinkled in.
 */
const SAMPLE_SIZE = 10;
function findNumericColumnIndex(
  columns: readonly string[],
  rows: ReadonlyArray<ReadonlyArray<unknown>>,
): number {
  for (let c = 0; c < columns.length; c++) {
    let sampled = 0;
    let numeric = 0;
    for (let r = 0; r < rows.length && sampled < SAMPLE_SIZE; r++) {
      const v = rows[r][c];
      if (v == null || v === "") continue;
      sampled++;
      if (asNumber(v) != null) numeric++;
    }
    if (sampled > 0 && numeric / sampled >= 0.8) return c;
  }
  return -1;
}

/**
 * Pick a categorical / label column — the first column that ISN'T
 * the chosen numeric one and whose values look like discrete labels
 * (most rows distinct, mostly strings). Falls back to column 0 if no
 * better choice surfaces.
 */
function findCategoryColumnIndex(
  columns: readonly string[],
  rows: ReadonlyArray<ReadonlyArray<unknown>>,
  excludeIdx: number,
): number {
  for (let c = 0; c < columns.length; c++) {
    if (c === excludeIdx) continue;
    let sampled = 0;
    let stringy = 0;
    for (let r = 0; r < rows.length && sampled < SAMPLE_SIZE; r++) {
      const v = rows[r][c];
      if (v == null || v === "") continue;
      sampled++;
      if (typeof v === "string" || asNumber(v) == null) stringy++;
    }
    if (sampled > 0 && stringy / sampled >= 0.5) return c;
  }
  // Fallback: column 0 if it isn't the numeric one, else -1.
  return excludeIdx === 0 ? -1 : 0;
}

/**
 * Coerce a cell to a finite number or null. Strings get parsed with
 * {@code Number()} — handles "12", "12.5", "1e3" cleanly. Booleans
 * and objects return null (treating them as numbers would be misleading
 * for a sum/top stat).
 */
function asNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const trimmed = v.trim();
    if (trimmed === "") return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function countNulls(rows: ReadonlyArray<ReadonlyArray<unknown>>): number {
  let n = 0;
  for (const row of rows) {
    for (const cell of row) {
      if (cell == null || cell === "") n++;
    }
  }
  return n;
}

/**
 * Compact-form number — "1.2K", "8.4M", "1.2B". Uses Intl when
 * available (every browser since ~2020 supports notation:"compact").
 * Falls back to the raw value for very old environments.
 */
function compactNumber(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (Math.abs(n) < 1000) {
    return Number.isInteger(n) ? n.toString() : n.toFixed(1);
  }
  try {
    return new Intl.NumberFormat(undefined, {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(n);
  } catch {
    return n.toLocaleString();
  }
}

function formatPercent(share: number): string {
  if (!Number.isFinite(share) || share <= 0) return "0%";
  if (share >= 0.995) return "100%";
  const pct = share * 100;
  return pct >= 10 ? `${Math.round(pct)}%` : `${pct.toFixed(1)}%`;
}
